import { commonUtils, timeUtils } from '@sato/components';
import { format, setDay, startOfWeek } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { toNumber } from 'lodash';

import {
  SerializedPlainDate,
  SerializedPlainDateTime,
  SerializedTime,
} from '@models/api/data.types';
import { localizationStore } from '@stores/localization';

/** Gets formatted time of day in hours and minutes */
export const formatTime = (date: string | Date) =>
  timeUtils.formatDate({
    date: new Date(date),
    dateFormat: timeUtils.DATE_FORMATS.TIME,
    locale: localizationStore.language,
  });

export const formatDateAndTime = (
  serializedDateTime: SerializedPlainDateTime
) => ({
  date: timeUtils.formatISO(serializedDateTime, { representation: 'date' }),
  time: timeUtils.formatDate({
    date: serializedDateTime,
    dateFormat: 'HH:mm',
  }),
});

export const formatDate = (
  date: Parameters<typeof timeUtils.formatDate>['0']['date']
) =>
  timeUtils.formatDate({
    date,
    dateFormat: timeUtils.DATE_FORMATS.DATE,
    locale: localizationStore.language,
  });

export const formatSerializedPlainDate = (date: Date): SerializedPlainDate =>
  format(date, 'yyyy-MM-dd');

/** Gets the formatted day of the week */
export const getDayOfWeek = (
  day: number,
  /** Format of the weekday.
   *
   * - `long`: "tiistaina"
   * - `short`: "ti"
   * - `standalone`: "tiistai"
   */
  format: 'long' | 'short' | 'standalone' = 'long'
): string => {
  if (format === 'standalone') {
    return localizationStore.getTranslation(
      weekdayTranslationKeyMap[day as DayOfWeek]
    );
  }

  return timeUtils.formatDate({
    date: setDay(new Date(), day),
    dateFormat: format === 'long' ? 'EEEE' : 'EEEEEE',
    locale: localizationStore.language,
  });
};

/** Formats date in a specific time zone. Target format: 2022-05-04T09:30:00
 *
 * @param {Date=} date
 * @param {string=} timeZone - Defaults to "Europe/Helsinki"
 */

export const formatDateToPlainDateInTimeZone = (
  date: Date,
  timeZone = 'Europe/Helsinki'
) => formatInTimeZone(date, timeZone, "yyyy-MM-dd'T'HH:mm:ss");

export const getStartOfWeek = (date: Date) =>
  startOfWeek(date, { weekStartsOn: 1 });

export enum DayOfWeek {
  SUNDAY = 0,
  MONDAY = 1,
  TUESDAY = 2,
  WEDNESDAY = 3,
  THURSDAY = 4,
  FRIDAY = 5,
  SATURDAY = 6,
}

const weekdayTranslationKeyMap = {
  [DayOfWeek.SUNDAY]: 'weekday.sunday',
  [DayOfWeek.MONDAY]: 'weekday.monday',
  [DayOfWeek.TUESDAY]: 'weekday.tuesday',
  [DayOfWeek.WEDNESDAY]: 'weekday.wednesday',
  [DayOfWeek.THURSDAY]: 'weekday.thursday',
  [DayOfWeek.FRIDAY]: 'weekday.friday',
  [DayOfWeek.SATURDAY]: 'weekday.saturday',
} satisfies Record<DayOfWeek, string>;

/**
 * Parses plain serialized time (hh:mm)
 *
 * @example parseSerializedTime("13:50") // { hours: 13, minutes: 50 }
 */
export const parseSerializedTime = (
  serializedTime: SerializedTime
): { hours: number; minutes: number } => {
  const [hours, minutes] = serializedTime.split(':').map(toNumber);

  if (commonUtils.isNullable(hours) || commonUtils.isNullable(minutes))
    throw Error(
      'Invalid time provided to `parseSerializedTime`: ' + serializedTime
    );

  return { hours, minutes };
};

export const formatRange = (props: {
  startTime: SerializedTime;
  endTime: SerializedTime;
}) => [props.startTime, props.endTime].join(' – ');
